import { useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useField, useForm } from 'react-final-form';
import { v1 as uuidv1 } from 'uuid';
import { FiUpload } from 'react-icons/fi';
import { RiDeleteBin6Line as BinIcon } from 'react-icons/ri';
import API from '../../../../rest/api';
import MeritoButton from '../../../../components/common/MeritoButtons/MeritoButtons';
import CircularLoader from '../../../../components/common/CircularLoader/CircularLoader';
import { checkPDFProtection } from '../../components/utils/functions';
import { validateFile } from '../../components/TableForm/TableUploadCell';
import { pdfProtectionStates, uploadStates, UnderwritingSteps } from '../../../../interfaces/interfaces';
import { UnderwritingFormContext } from '../../context/UnderwritingFormContext';
import styles from './GstCertificateUpload.module.scss';

// A portal-based tooltip, scoped to this file. The shared Tooltip component
// positions itself with CSS `position: absolute` relative to an in-flow
// wrapper, which means it's still laid out inside the GSTIN list's
// scrollable container — any part of it that visually escapes the row
// still counts toward that container's scrollWidth/scrollHeight, showing
// spurious scrollbars. Rendering into document.body via a portal, positioned
// from the trigger's actual screen coordinates, sidesteps that entirely.
function IconTooltip({ children, content }: { children: ReactNode; content: ReactNode }) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({ top: rect.top - 8, left: rect.left + rect.width / 2 });
  };

  return (
    <span
      ref={anchorRef}
      className={styles.tooltipAnchor}
      onMouseEnter={show}
      onMouseLeave={() => setCoords(null)}
    >
      {children}
      {coords &&
        createPortal(
          <div className={styles.floatingTooltip} style={{ top: coords.top, left: coords.left }}>
            {content}
          </div>,
          document.body
        )}
    </span>
  );
}

function truncateMiddle(name: string, maxLength = 44): string {
  if (!name || name.length <= maxLength) return name;
  const extMatch = name.match(/\.[^.]{1,6}$/);
  const ext = extMatch ? extMatch[0] : '';
  const base = ext ? name.slice(0, -ext.length) : name;
  const keep = Math.max(4, Math.floor((maxLength - ext.length - 3) / 2));
  return `${base.slice(0, keep)}...${base.slice(-keep)}${ext}`;
}

type Props = {
  fieldName: string;
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  formContext?: any;
};

const STEP = UnderwritingSteps.company;
const DOCUMENT_TYPE = 'companyDocuments';

export default function GstCertificateUpload({
  fieldName,
  setNewUploadedDocIds,
  setDeleteDocIds,
  formContext,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { change } = useForm();
  const { handleUnderwritingFileJobs, underwritingFileJobs, sendFileJobToApi } = useContext(
    formContext ? formContext : UnderwritingFormContext
  ) as any;

  const { input: { value: fieldValue } } = useField(fieldName, { subscription: { value: true } });

  const getJobDetails = useCallback(
    () => underwritingFileJobs?.[STEP]?.[fieldName] ?? null,
    [underwritingFileJobs, fieldName]
  );
  const jobDetails = getJobDetails();
  const isUploading = jobDetails?.status === uploadStates.uploading;
  const uploadedFile = fieldValue?.uploadedFile;
  const hasFile = uploadedFile || fieldValue?.selectedFile instanceof File;

  const uploadFileToS3 = async (fileData: File, fileFormJob: any) => {
    const formData = new FormData();
    formData.append('documentType', DOCUMENT_TYPE);
    formData.append('file', fileData);
    const data = await API.post('/app/uploadDocs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);

    const isPdfProtected =
      fileData.type === 'application/pdf' ? await checkPDFProtection(fileData) : pdfProtectionStates.passwordNotRequired;
    const needsPassword = isPdfProtected === pdfProtectionStates.passwordRequired;

    const jd = await sendFileJobToApi(STEP, fieldName, {
      s3FileKey: data?.Key,
      fileMetaData: {
        fileName: fileData.name,
        fileType: fileData.type,
        requiredPassword: needsPassword,
        password: null,
      },
      status: needsPassword ? uploadStates.requiredPassword : uploadStates.doneUploading,
    });

    setNewUploadedDocIds((prev) => [...prev, jd?.docId]);
    change(fieldName, {
      ...fileFormJob,
      uploadedFile: { fileKey: data?.Key, fileName: fileData.name, fileType: fileData.type, docId: jd?.id },
    });
  };

  const uploadFile = (fileData: File) => {
    const fileFormObj = { selectedFile: fileData, uploadedFile: null };
    const fileJob = {
      jobId: uuidv1(),
      step: STEP,
      section: DOCUMENT_TYPE,
      formFieldName: fieldName,
      s3FileKey: null,
      fileMetaData: { fileName: fileData.name, fileType: fileData.type, requiredPassword: false, password: null },
      status: uploadStates.uploading,
    };
    change(fieldName, fileFormObj);
    handleUnderwritingFileJobs(STEP, fieldName, fileJob);
    uploadFileToS3(fileData, fileFormObj);
  };

  const removeFile = () => {
    if (uploadedFile?.docId) {
      setDeleteDocIds((prev) => [...prev, uploadedFile.docId]);
    }
    change(fieldName, null);
    // Clear the tracked upload job too — otherwise a stale REQUIRED_PASSWORD
    // job (or any other status) survives the delete and the password panel
    // in GstDetails.tsx keeps showing for a certificate that's gone.
    handleUnderwritingFileJobs(STEP, fieldName, null);
  };

  if (isUploading) {
    return (
      <div className={styles.loaderWrap}>
        <CircularLoader />
      </div>
    );
  }

  if (hasFile) {
    const fileName = uploadedFile?.fileName || (fieldValue?.selectedFile as File)?.name;
    return (
      <div className={styles.certUploadRoot}>
        <IconTooltip
          content={
            <div className={styles.uploadedTooltip}>
              <div className={styles.uploadedLabel}>File uploaded:</div>
              <div className={styles.fileName}>{truncateMiddle(fileName)}</div>
              <div className={styles.tooltipDivider} />
              <div className={styles.removeHint}>Click to remove</div>
            </div>
          }
        >
          <MeritoButton
            type="tertiary"
            size="md"
            onlyIcon
            className={styles.removeIcon}
            icon={<BinIcon />}
            onClick={removeFile}
          />
        </IconTooltip>
      </div>
    );
  }

  return (
    <div className={styles.certUploadRoot}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".jpg,.jpeg,.png,.pdf"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file || validateFile(file)) return;
          uploadFile(file);
        }}
      />
      <IconTooltip content="Upload GST Certificate">
        <MeritoButton
          type="tertiary"
          size="md"
          onlyIcon
          icon={<FiUpload />}
          onClick={() => fileInputRef.current?.click()}
        />
      </IconTooltip>
    </div>
  );
}
